const normalizeName = (value?: string | null): string => (value ?? '').trim().toLowerCase();

// Normalises a DOB to a `yyyy-MM-dd` key (UTC calendar date) for equality checks,
// ignoring any time component. Avoids the cost of formatting two dates just to compare.
const dobKey = (value?: string | null): string => {
    if (!value) {
        return '';
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return '';
    }

    return `${date.getUTCFullYear()}-${date.getUTCMonth()}-${date.getUTCDate()}`;
};

// Builds a display name from the device-set first/last name parts.
export const getDeviceName = (
    deviceFirstName?: string | null,
    deviceLastName?: string | null
): string => [deviceFirstName, deviceLastName].filter(Boolean).join(' ').trim();

// True when the device-set name is present and differs from the form-filled name.
export const hasDeviceNameMismatch = (
    firstName?: string | null,
    lastName?: string | null,
    deviceFirstName?: string | null,
    deviceLastName?: string | null
): boolean => {
    if (!deviceFirstName && !deviceLastName) {
        return false;
    }

    return (
        normalizeName(firstName) !== normalizeName(deviceFirstName) ||
        normalizeName(lastName) !== normalizeName(deviceLastName)
    );
};

// True when both DOBs resolve to a calendar date and those dates differ. A missing or unparseable
// value yields no key, and we can't assert the device value differs from one we don't have.
export const hasDeviceDobMismatch = (
    dateOfBirth?: string | null,
    deviceDateOfBirth?: string | null
): boolean => {
    const formKey = dobKey(dateOfBirth);
    const deviceKey = dobKey(deviceDateOfBirth);

    if (!formKey || !deviceKey) {
        return false;
    }

    return formKey !== deviceKey;
};

// Tooltip copy describing which device-set value(s) differ from the form-filled data.
export const getMismatchContent = (nameMismatch: boolean, dobMismatch: boolean): string => {
    if (nameMismatch && dobMismatch) {
        return 'The name and date of birth set on the device differ from the form-filled values.';
    }

    if (nameMismatch) {
        return 'The name set on the device differs from the form-filled name.';
    }

    if (dobMismatch) {
        return 'The date of birth set on the device differs from the form-filled date of birth.';
    }

    return '';
};

export type DeviceMismatchFields = {
    firstName?: string | null;
    lastName?: string | null;
    dateOfBirth?: string | null;
    deviceFirstName?: string | null;
    deviceLastName?: string | null;
    deviceDateOfBirth?: string | null;
};

export type DeviceMismatch = {
    nameMismatch: boolean;
    dobMismatch: boolean;
    hasMismatch: boolean;
    content: string;
};

// Single entry point: resolves name/DOB mismatch flags plus the tooltip copy.
export const getDeviceMismatch = (fields: DeviceMismatchFields): DeviceMismatch => {
    const nameMismatch = hasDeviceNameMismatch(
        fields.firstName,
        fields.lastName,
        fields.deviceFirstName,
        fields.deviceLastName
    );
    const dobMismatch = hasDeviceDobMismatch(fields.dateOfBirth, fields.deviceDateOfBirth);

    return {
        nameMismatch,
        dobMismatch,
        hasMismatch: nameMismatch || dobMismatch,
        content: getMismatchContent(nameMismatch, dobMismatch)
    };
};
